import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';

/**
 * Wraps a list table with the shared behaviour: horizontal scroll on narrow screens,
 * an optional sticky header (give it a maxHeight), a loading skeleton, and the empty state.
 *
 *   <lb-table [loading]="loading" [empty]="rows.length === 0" emptyTitle="No vendors yet" emptyIcon="fa-building-o">
 *     <table class="table table-hover">...</table>
 *   </lb-table>
 *
 * Right-align numbers with class="num" on th/td.
 */
@Component({
  selector: 'lb-table',
  encapsulation: ViewEncapsulation.None,
  template: `
    <div *ngIf="loading && empty" class="lb-table-skeleton placeholder-glow" aria-busy="true" aria-label="Loading">
      <div class="placeholder col-12 lb-table-skeleton-head"></div>
      <div *ngFor="let r of skeleton" class="placeholder col-12"></div>
    </div>
    <lb-empty-state *ngIf="!loading && empty" [icon]="emptyIcon" [title]="emptyTitle" [compact]="compact">
      {{ emptyText }}
      <ng-content select="[lbEmptyActions]"></ng-content>
    </lb-empty-state>
    <div *ngIf="!empty" class="table-responsive lb-table" [class.lb-table-sticky]="!!maxHeight" [style.max-height]="maxHeight"
         [class.lb-table-refreshing]="loading">
      <ng-content></ng-content>
    </div>
  `,
  styles: [`
    .lb-table-sticky thead th {
      position: sticky;
      top: 0;
      z-index: 2;
    }
    .lb-table-refreshing {
      opacity: 0.6;
      transition: opacity 0.15s ease;
    }
    .lb-table-skeleton {
      display: grid;
      gap: 0.6rem;
      padding: var(--lb-space-3) 0;
    }
    .lb-table-skeleton .placeholder {
      height: 1.6rem;
      border-radius: var(--lb-radius-sm);
    }
    .lb-table-skeleton .lb-table-skeleton-head {
      height: 2rem;
      opacity: 0.8;
    }
  `]
})
export class LbTableComponent {
  @Input() loading = false;
  @Input() empty = false;
  @Input() emptyTitle = 'Nothing to show';
  @Input() emptyText = '';
  @Input() emptyIcon = 'fa-inbox';
  @Input() compact = false;
  /** e.g. "70vh": the table scrolls inside this height and its header stays in view */
  @Input() maxHeight: string | null = null;
  @Input() skeletonRows = 5;

  get skeleton(): number[] {
    return Array.from({ length: this.skeletonRows }, (_, i) => i);
  }
}

/**
 * A sortable column header: <th lbSort="deliveryDate" [sortBy]="sortColumn" [sortDir]="sortDirection" (sort)="setSort($event)">Delivery</th>
 * Renders a real button (keyboard reachable) and sets aria-sort on the header cell.
 */
@Component({
  selector: 'th[lbSort]',
  encapsulation: ViewEncapsulation.None,
  host: { '[attr.aria-sort]': 'ariaSort', 'class': 'lb-sort-th' },
  template: `
    <button type="button" class="lb-sort-button" (click)="sort.emit(lbSort)">
      <ng-content></ng-content>
      <i class="fa" [ngClass]="icon" aria-hidden="true"></i>
    </button>
  `,
  styles: [`
    .lb-sort-button {
      all: unset;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      font-weight: inherit;
      white-space: nowrap;
      border-radius: var(--lb-radius-sm);
    }
    .lb-sort-button:focus-visible {
      outline: 2px solid var(--lb-focus);
      outline-offset: 2px;
    }
    .lb-sort-button .fa {
      color: var(--lb-text-muted);
      font-size: 0.8em;
    }
    th.num .lb-sort-button {
      flex-direction: row-reverse;
    }
  `]
})
export class LbSortHeaderComponent {
  @Input() lbSort = '';
  @Input() sortBy = '';
  @Input() sortDir: 'asc' | 'desc' | string = 'asc';
  @Output() sort = new EventEmitter<string>();

  get active(): boolean {
    return this.sortBy === this.lbSort;
  }

  get ariaSort(): string {
    if (!this.active) return 'none';
    return this.sortDir === 'desc' ? 'descending' : 'ascending';
  }

  get icon(): string {
    if (!this.active) return 'fa-sort';
    return this.sortDir === 'desc' ? 'fa-sort-desc' : 'fa-sort-asc';
  }
}
